import {
  GeometryType,
  Model as TrbModel,
} from '@technology/web3d-plugin-trimbim/dist/esnext/TrimBim_generated';
import { ByteBuffer } from 'flatbuffers';
import { Vector3 } from '@technology/web3d';
import { TrajectorySegment } from './trimbim-alignment.types';

export const VALUES_PER_SEGMENT = 6;
const ZERO_LENGTH_EPSILON = 1e-8;

export async function getGeometryInstancesByEntityId(
  flatModel: TrbModel
): Promise<Map<number, number[]>> {
  const geometryInstancesByEntityId: Map<number, number[]> = new Map();
  const geometry = flatModel.geometry();
  if (!geometry) {
    return new Map();
  }

  for (
    let instanceIndex = 0;
    instanceIndex < (geometry?.instancesLength() || 0);
    instanceIndex++
  ) {
    const instance = geometry.instances(instanceIndex);
    if (instance) {
      const entityId: number = instance.entityId();
      const instances = geometryInstancesByEntityId.get(entityId) || [];
      instances.push(instanceIndex);
      geometryInstancesByEntityId.set(entityId, instances);
    }
  }
  return geometryInstancesByEntityId;
}

interface EntityLineSegments {
  segments: TrajectorySegment[];
  zeroLengthSegmentsCount: number;
}

/** World-space line segments of one entity, in instance order. */
export function collectEntityLineSegments(
  flatModel: TrbModel,
  entity: number,
  instances: readonly number[]
): EntityLineSegments {
  const geometry = flatModel.geometry();
  const result: EntityLineSegments = {
    segments: [],
    zeroLengthSegmentsCount: 0,
  };
  if (!geometry) return result;

  const globalOrigin = entityGlobalOrigin(flatModel, entity);

  for (const instanceIndex of instances) {
    const instance = geometry.instances(instanceIndex);
    if (!instance) continue;
    const definition = geometry.definitions(instance.definitionId());
    if (definition?.type() !== GeometryType.GraphicsContainer) continue;

    const localOrigin = geometry
      .localPlacements(instance.localPlacementId())
      ?.origin();
    const offsetX = globalOrigin.x + (localOrigin?.x() ?? 0);
    const offsetY = globalOrigin.y + (localOrigin?.y() ?? 0);
    const offsetZ = globalOrigin.z + (localOrigin?.z() ?? 0);

    const graphicsContainer = geometry.graphicsContainers(definition.index());
    const count = graphicsContainer?.lineSegmentsLength() ?? 0;
    for (let i = 0; i < count; i++) {
      const lineSegment = graphicsContainer?.lineSegments(i);
      const start = lineSegment?.start();
      const end = lineSegment?.end();
      if (!start || !end) continue;

      const worldStart = new Vector3(
        offsetX + start.x(),
        offsetY + start.y(),
        offsetZ + start.z()
      );
      const worldEnd = new Vector3(
        offsetX + end.x(),
        offsetY + end.y(),
        offsetZ + end.z()
      );
      result.segments.push({ start: worldStart, end: worldEnd });
      if (worldStart.distanceToSquared(worldEnd) < ZERO_LENGTH_EPSILON) {
        result.zeroLengthSegmentsCount++;
      }
    }
  }
  return result;
}

export function segmentsToVertices(
  segments: readonly TrajectorySegment[]
): Float64Array {
  const vertices = new Float64Array(segments.length * VALUES_PER_SEGMENT);
  segments.forEach(({ start, end }, i) => {
    vertices.set(
      [start.x, start.y, start.z, end.x, end.y, end.z],
      i * VALUES_PER_SEGMENT
    );
  });
  return vertices;
}

export function verticesToSegments(
  vertices: Float64Array
): TrajectorySegment[] {
  const segments: TrajectorySegment[] = [];
  for (let i = 0; i + VALUES_PER_SEGMENT <= vertices.length; ) {
    segments.push({
      start: new Vector3(vertices[i++], vertices[i++], vertices[i++]),
      end: new Vector3(vertices[i++], vertices[i++], vertices[i++]),
    });
  }
  return segments;
}

/**
 * Re-reads the segment vertices of the given section entities from a trimbim.
 * Key = section entity id.
 */
export async function extractSectionSegmentVertices(
  trimbim: ArrayBuffer,
  entityIds: readonly number[]
): Promise<Map<number, Float64Array>> {
  const flatModel = TrbModel.getRootAsModel(
    new ByteBuffer(new Uint8Array(trimbim))
  );
  const instancesByEntity = await getGeometryInstancesByEntityId(flatModel);
  const result = new Map<number, Float64Array>();
  for (const entityId of entityIds) {
    const instances = instancesByEntity.get(entityId);
    if (!instances) continue;
    const { segments } = collectEntityLineSegments(
      flatModel,
      entityId,
      instances
    );
    result.set(entityId, segmentsToVertices(segments));
  }
  return result;
}

function entityGlobalOrigin(
  flatModel: TrbModel,
  entity: number
): { x: number; y: number; z: number } {
  const origin = flatModel
    .entities()
    ?.entities(entity)
    ?.globalPlacement()
    ?.origin();
  return { x: origin?.x() ?? 0, y: origin?.y() ?? 0, z: origin?.z() ?? 0 };
}
